import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge } from '@tremor/react';
import { Server, Database, Cpu, AlertTriangle } from 'lucide-react';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';
import {
  formatRangeShort,
  useDateRange,
  useDateRangeKey,
} from '@/lib/dateRange';

interface HealthResponse {
  as_of: string;
  overall_status: 'UP' | 'DEGRADED' | 'DOWN';
  db: { status: string; latency_ms: number; version: string | null };
  queue: {
    by_status: Array<{ status: string; count: number }>;
    active_jobs: Array<{
      id: number;
      filename: string;
      status: string;
      status_message: string | null;
      user_id: number | null;
      created_at: string | null;
    }>;
    stuck_count: number;
    failures_24h: number;
  };
  workers: {
    ports: number[];
    up: number;
    total: number;
    probes: Array<{
      port: number;
      status: string;
      http_code?: number;
      latency_ms: number;
      error?: string;
    }>;
  };
}

interface FailuresResponse {
  by_status: Array<{ status: string; count: number }>;
  top_messages: Array<{ message: string; count: number; last_seen: string | null }>;
  by_day: Array<{ day: string; count: number }>;
}

const statusColor = (s: string) =>
  s === 'UP' ? 'emerald' : s === 'DEGRADED' ? 'amber' : 'red';

export function InfraPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();

  // Health is "now"-based and shouldn't depend on the user-selected window —
  // we always want a live view of DB/workers/queue.
  const health = useQuery<HealthResponse>({
    queryKey: ['infra.health'],
    queryFn: async () => (await api().get('/infra/health')).data,
    refetchInterval: 15_000,
  });

  const failures = useQuery<FailuresResponse>({
    queryKey: ['infra.failures', rangeKey],
    queryFn: async () =>
      (await api().get('/infra/failures', { params: apiParams })).data,
  });

  const errMsg = health.error
    ? extractErrorMessage(health.error, 'erro ao carregar saúde')
    : null;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <Title>Infra</Title>
          <Text>Saúde de DB, fila de jobs, workers PJe-Calc.</Text>
        </div>
        {health.data && (
          <Badge color={statusColor(health.data.overall_status)}>
            {health.data.overall_status}
          </Badge>
        )}
      </header>

      <Grid numItems={1} numItemsLg={3} className="gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-blue-300" />
              <Title className="text-base">PostgreSQL</Title>
            </div>
            {health.data && (
              <Badge color={statusColor(health.data.db.status)}>
                {health.data.db.status}
              </Badge>
            )}
          </div>
          {health.isLoading && <LoadingSkeleton rows={2} />}
          {health.data && (
            <div className="mt-2 text-sm text-slate-300">
              <div>
                Latência: <span className="font-mono">{health.data.db.latency_ms}ms</span>
              </div>
              {health.data.db.version && (
                <div className="mt-1 truncate text-xs text-slate-500">
                  {health.data.db.version}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cpu className="h-4 w-4 text-purple-300" />
              <Title className="text-base">Workers PJe-Calc</Title>
            </div>
            {health.data && (
              <Badge color={health.data.workers.up === health.data.workers.total ? 'emerald' : health.data.workers.up > 0 ? 'amber' : 'red'}>
                {health.data.workers.up}/{health.data.workers.total} UP
              </Badge>
            )}
          </div>
          {health.isLoading && <LoadingSkeleton rows={3} />}
          {health.data && (
            <div className="mt-2 space-y-1 text-sm">
              {health.data.workers.probes.map((p) => (
                <div key={p.port} className="flex items-center justify-between text-xs">
                  <span className="font-mono text-slate-300">:{p.port}</span>
                  <span className="text-slate-500">
                    {p.latency_ms}ms
                  </span>
                  <Badge color={p.status === 'UP' ? 'emerald' : 'red'} size="xs">
                    {p.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-amber-300" />
              <Title className="text-base">Fila</Title>
            </div>
            {health.data && (health.data.queue.stuck_count > 0 || health.data.queue.failures_24h > 5) && (
              <Badge color="red" icon={AlertTriangle}>
                Atenção
              </Badge>
            )}
          </div>
          {health.isLoading && <LoadingSkeleton rows={3} />}
          {health.data && (
            <div className="mt-2 space-y-1 text-sm text-slate-300">
              <div className="flex justify-between">
                <span>Travados (&gt;30min)</span>
                <span className={health.data.queue.stuck_count > 0 ? 'text-red-300' : ''}>
                  {health.data.queue.stuck_count}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Falhas (24h)</span>
                <span className={health.data.queue.failures_24h > 5 ? 'text-red-300' : ''}>
                  {health.data.queue.failures_24h}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Ativos agora</span>
                <span>{health.data.queue.active_jobs.length}</span>
              </div>
            </div>
          )}
        </Card>
      </Grid>

      {errMsg && (
        <Card className="border-red-800/50 bg-red-900/20">
          <Text className="text-red-300">{errMsg}</Text>
        </Card>
      )}

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title className="text-base">Status dos jobs (7 dias)</Title>
          {health.isLoading && <LoadingSkeleton rows={3} />}
          {health.data && (
            <div className="mt-3 space-y-1">
              {health.data.queue.by_status.map((s) => (
                <div key={s.status} className="flex items-center justify-between border-b border-slate-800/50 py-1.5 text-sm">
                  <Badge color={s.status.includes('ERROR') || s.status.includes('FAIL') ? 'red' : s.status === 'DONE' || s.status === 'COMPLETED' ? 'emerald' : 'blue'}>
                    {s.status}
                  </Badge>
                  <span className="font-mono text-slate-200">
                    {new Intl.NumberFormat('pt-BR').format(s.count)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <Title className="text-base">Jobs ativos</Title>
          {health.isLoading && <LoadingSkeleton rows={3} />}
          {health.data && (
            <div className="mt-3 max-h-64 space-y-1 overflow-y-auto">
              {health.data.queue.active_jobs.length === 0 && (
                <Text className="text-slate-400">Nenhum job em execução.</Text>
              )}
              {health.data.queue.active_jobs.map((j) => (
                <div key={j.id} className="rounded border border-slate-800 px-3 py-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-slate-300">#{j.id}</span>
                    <Badge color="blue" size="xs">{j.status}</Badge>
                  </div>
                  <div className="mt-0.5 truncate text-slate-400">
                    {j.filename}
                  </div>
                  {j.status_message && (
                    <div className="mt-0.5 truncate text-[10px] text-slate-500">
                      {j.status_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </Grid>

      <Card>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-300" />
          <Title className="text-base">Top erros · {formatRangeShort(range)}</Title>
        </div>
        {failures.isLoading && <LoadingSkeleton rows={3} />}
        {!failures.isLoading && (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs text-slate-400">
                <th className="py-2">Mensagem</th>
                <th>Ocorrências</th>
                <th>Último visto</th>
              </tr>
            </thead>
            <tbody>
              {failures.data?.top_messages.map((m, idx) => (
                <tr key={idx} className="border-b border-slate-800/50">
                  <td className="py-2 max-w-[600px] truncate text-slate-300" title={m.message}>
                    {m.message}
                  </td>
                  <td>{m.count}</td>
                  <td className="text-xs text-slate-500">
                    {m.last_seen ? new Date(m.last_seen).toLocaleString('pt-BR') : '—'}
                  </td>
                </tr>
              ))}
              {failures.data?.top_messages.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-4 text-center text-emerald-400">
                    Sem erros no período selecionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
